import { KlusInputFormFieldNames } from "@personeel/enums/KlusInputFormFieldNames";
import { CreateKlusModel } from "@personeel/models/CreateKlusModel";
import { isValidTime } from "@personeel/utils/DateTimeUtils";
import { useSelectedTimeStore } from "@shared/common/contexts/SelectedTime/useSelectedTimeStore";
import { DateTime } from "luxon";
import z from "zod";
import { IsKlusInDienst } from "./IsKlusInDienstValidator";
import { useKlusSoortenStore } from "@personeel/stores/KlusSoortenStore";

function begintKlusBinnenScenario(klus: CreateKlusModel): string {
  const { scenarioInterval } = useSelectedTimeStore.getState();

  if (!scenarioInterval || !klus.beginDatum.trim() || !klus.beginTijd.trim()) {
    return "";
  }

  const parseDateTime = (date: string, time: string) =>
    DateTime.fromFormat(`${date} ${time}`, "yyyy-MM-dd HH:mm");

  const klusBegin = parseDateTime(klus.beginDatum, klus.beginTijd);
  if (!klusBegin.isValid) return "";

  const isBinnenInterval = klusBegin >= scenarioInterval.start && klusBegin <= scenarioInterval.end;
  return isBinnenInterval ? "" : "Klus moet beginnen binnen het geopende plan";
}

function isValidDate(date: string): boolean {
  return DateTime.fromFormat(date, "yyyy-MM-dd").isValid;
}

const createKlusModelSchema = z.instanceof(CreateKlusModel).superRefine((klus, ctx) => {
  type Field = {
    field: KlusInputFormFieldNames;
    message: string;
    valueValidator?: (value: string) => boolean;
    klusValidator?: (klus: CreateKlusModel) => string;
    invalidMessage?: string;
  };

  const klusSoorten = useKlusSoortenStore.getState().klusSoorten;

  const fieldsToValidate: Field[] = [
    { field: KlusInputFormFieldNames.KlusSoort, message: "Klussoort mag niet leeg zijn" },
    {
      field: KlusInputFormFieldNames.Begindatum,
      message: "Klus begint op is een verplicht veld",
      valueValidator: isValidDate,
      invalidMessage: "Ongeldige datum ingevoerd.",
    },
    {
      field: KlusInputFormFieldNames.Einddatum,
      message: "Klus eindigt op is een verplicht veld",
      valueValidator: isValidDate,
      invalidMessage: "Ongeldige datum ingevoerd.",
    },
    {
      field: KlusInputFormFieldNames.Begintijd,
      message: "Starttijd is een verplicht veld",
      valueValidator: isValidTime,
      invalidMessage: "Ongeldige tijd ingevoerd.",
    },
    {
      field: KlusInputFormFieldNames.Eindtijd,
      message: "Eindtijd is een verplicht veld",
      valueValidator: isValidTime,
      invalidMessage: "Ongeldige tijd ingevoerd.",
    },
    {
      field: KlusInputFormFieldNames.SpoorStart,
      message: "Klus Startlocatie is een verplicht veld",
    },
    {
      field: KlusInputFormFieldNames.SpoorEind,
      message:
        klus.klusSoort == "TAXI" ||
        klusSoorten.some(
          (ks) => ks.klusCode === klus.klusSoort.trim() && ks.klusType === "BEWEGINGSGEBONDEN",
        )
          ? "Klus Eindlocatie is een verplicht veld"
          : "",
    },
    {
      message: "",
      field: KlusInputFormFieldNames.Dienst,
      klusValidator: IsKlusInDienst,
    },
    {
      message: "",
      field: KlusInputFormFieldNames.Begindatum,
      klusValidator: begintKlusBinnenScenario,
    },
    {
      field: KlusInputFormFieldNames.Rangeerbeweging,
      message: klusSoorten.some(
        (ks) => ks.klusCode === klus.klusSoort.trim() && ks.klusType === "BEWEGINGSGEBONDEN",
      )
        ? "Beweging is een verplicht veld voor beweginggebonden klus"
        : "",
    },
  ];

  for (const {
    field,
    message,
    valueValidator,
    klusValidator,
    invalidMessage,
  } of fieldsToValidate) {
    const value = klus[field]?.trim();

    if (!value && message.trim() !== "") {
      ctx.addIssue({
        code: "custom",
        message: message,
        path: [field],
      });
      continue;
    }

    if (valueValidator && !valueValidator(value as string)) {
      ctx.addIssue({
        code: "custom",
        message: invalidMessage || "Ongeldige waarde",
        path: [field],
      });
    }

    if (klusValidator) {
      const error = klusValidator(klus);
      if (error) {
        ctx.addIssue({
          code: "custom",
          message: error,
          path: [field],
        });
      }
    }
  }
});

export type KlusInputErrors = {
  [k: string]: string;
};

function zodErrorToErrorsObject(error: z.ZodError): KlusInputErrors {
  return Object.fromEntries(
    error.issues.flatMap((issue) =>
      issue.path[0] && issue.message.trim() !== "" ? [[String(issue.path[0]), issue.message]] : [],
    ),
  );
}

export const valideerKlusInput = (klus: CreateKlusModel) => {
  const result = createKlusModelSchema.safeParse(new CreateKlusModel(klus));
  if (result.error) {
    return zodErrorToErrorsObject(result.error);
  }
  return {};
};
